import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import {
  Box,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCreatePatient } from './hooks';
import { t } from 'i18next';
import {
  BuildingSearchFormField,
  DatepickerFormField,
  PracticeSearchFormField,
  ProviderSearchFormField,
  SelectFormField,
  TextFormField,
} from 'components/DynamicFormFields';
import React from 'react';
import { USStateSelect } from 'components/USStateSelect';
import { CustomModal, FormGooglePlacesAutocomplete } from '@components';
import { theme } from '@store';

export const CreatePatient = () => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    buildingUnitOptions,
    genderOptions,
    handleSelectLocation,
  } = useCreatePatient();

  const { control, getValues } = formMethods;
  const livesAtHome = getValues('lives_at_home') === 'true';
  const providerType = getValues('provider_type');

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
        disabled
      >
        {t('patients.addModal.openModalButtonLabel')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('patients.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <TextFormField
                name='first_name'
                label={t('patients.addModal.firstName')}
              />

              <TextFormField
                name='middle_name'
                label={t('patients.addModal.middleName')}
                optional={true}
              />
            </Box>
            <TextFormField
              name='last_name'
              label={t('patients.addModal.lastName')}
            />
            <TextFormField
              name='preferred_name'
              label={t('patients.addModal.preferredName')}
              optional
            />
            <Typography
              variant='caption'
              sx={{ color: 'text.disabled', px: 1, mt: '-16px' }}
            >
              {t('patients.addModal.preferredNameHelperText')}
            </Typography>
            <SelectFormField
              name='gender'
              label={t('patients.addModal.gender.fieldLabel')}
              options={genderOptions}
            />
            <DatepickerFormField
              name='birthdate'
              label={t('patients.addModal.birthdate')}
              optional={false}
            />
            <PracticeSearchFormField
              name='practice_id'
              placeholder={t('patients.addModal.practice')}
              optional={false}
            />
            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('patients.addModal.primaryCarePhysician')}
            </Typography>
            <Controller
              name={'provider_type'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      key={'Internal option'}
                      value={'Internal'}
                      control={<Radio value={'Internal'} />}
                      label={t('patients.addModal.provider_type.internal')}
                    />
                    <FormControlLabel
                      key={'External option'}
                      value={'External'}
                      control={<Radio value={'External'} />}
                      label={t('patients.addModal.provider_type.external')}
                    />
                    <FormControlLabel
                      key={'Unknown option'}
                      value={'Unknown'}
                      control={<Radio value={'Unknown'} />}
                      label={t('patients.addModal.provider_type.unknown')}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <React.Fragment key={'internal-provider-section'}>
              {providerType === 'Internal' && (
                <ProviderSearchFormField
                  name='provider_id'
                  placeholder={t('patients.addModal.internalPCP')}
                />
              )}
            </React.Fragment>
            <React.Fragment key={'external-provider-section'}>
              {providerType === 'External' && (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      gap: '0.5rem',
                    }}
                  >
                    <TextFormField
                      name='provider_first_name'
                      label={t('patients.addModal.providerFirstName')}
                    />

                    <TextFormField
                      name='provider_last_name'
                      label={t('patients.addModal.providerLastName')}
                    />
                  </Box>
                  <TextFormField
                    name='provider_phone'
                    label={t('patients.addModal.providerPhone')}
                  />
                </>
              )}
            </React.Fragment>
            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('patients.addModal.contactInformation')}
            </Typography>

            <Controller
              name={'lives_at_home'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      key={'Home option'}
                      value={'true'}
                      control={<Radio value={'true'} />}
                      label={t('patients.addModal.lives_at_home.home')}
                    />
                    <FormControlLabel
                      key={'Building option'}
                      value={'false'}
                      control={<Radio value={'false'} />}
                      label={t('patients.addModal.lives_at_home.building')}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <React.Fragment key={'lives-at-home-section'}>
              {livesAtHome === true ? (
                <>
                  <FormGooglePlacesAutocomplete
                    required
                    onChange={handleSelectLocation}
                  />
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      gap: '0.5rem',
                    }}
                  >
                    <Controller
                      name='street'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type='text'
                          label={t('patients.addModal.street')}
                          variant='outlined'
                          error={!isNil(error)}
                          helperText={error ? error.message : null}
                          required
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name='suite'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type='text'
                          label={t('patients.addModal.suite')}
                          variant='outlined'
                          error={!isNil(error)}
                          helperText={error ? error.message : null}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      gap: '0.5rem',
                    }}
                  >
                    <Controller
                      name='city'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type='text'
                          label={t('patients.addModal.city')}
                          variant='outlined'
                          error={!isNil(error)}
                          helperText={error ? error.message : null}
                          required
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name='state'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <USStateSelect
                          {...field}
                          error={!isNil(error)}
                          helperText={error ? error.message : ''}
                          required
                        />
                      )}
                    />
                  </Box>
                  <Controller
                    name='zip_code'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type='text'
                        label={t('patients.addModal.zip')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name='county'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type='text'
                        label={t('patients.addModal.county')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        fullWidth
                      />
                    )}
                  />
                </>
              ) : (
                <>
                  <BuildingSearchFormField
                    name='building_id'
                    placeholder={t('patients.addModal.building')}
                    optional={false}
                  />

                  <SelectFormField
                    name='building_unit'
                    label={t('patients.addModal.building_unit.fieldLabel')}
                    options={buildingUnitOptions}
                  />

                  <Controller
                    name='room'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type='text'
                        label={t('patients.addModal.room')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        fullWidth
                      />
                    )}
                  />
                </>
              )}
            </React.Fragment>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting}
              >
                {t('general.save')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
